/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPut } from '../../services/apiClient';
import { extractErrorMessage, logError } from '../../utils/errorHandler';
import { getAuthToken } from '../../services/authStorage';

const NotificationsPanel = ({ language, onClose, isOpen }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const t = (key) => {
    const translations = {
      ar: {
        title: 'الإشعارات', markAllRead: 'تحديد الكل كمقروء', noNotifications: 'لا توجد إشعارات',
        all: 'الكل', unread: 'غير مقروء', loading: 'جاري التحميل...', justNow: 'الآن',
        minutesAgo: 'منذ {n} دقيقة', hoursAgo: 'منذ {n} ساعة', daysAgo: 'منذ {n} يوم',
        invoice: 'فاتورة', payment: 'دفعة', inventory: 'مخزون', approval: 'اعتماد', system: 'نظام',
        financial: 'مالي', hr: 'موارد بشرية', manufacturing: 'تصنيع'
      },
      en: {
        title: 'Notifications', markAllRead: 'Mark all as read', noNotifications: 'No notifications',
        all: 'All', unread: 'Unread', loading: 'Loading...', justNow: 'Just now',
        minutesAgo: '{n} minutes ago', hoursAgo: '{n} hours ago', daysAgo: '{n} days ago',
        invoice: 'Invoice', payment: 'Payment', inventory: 'Inventory', approval: 'Approval', system: 'System',
        financial: 'Financial', hr: 'HR', manufacturing: 'Manufacturing'
      }
    };
    return translations[language]?.[key] || key;
  };

  const fetchNotifications = useCallback(async () => {
    if (!isOpen) return;
    if (!getAuthToken('erp')) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      const response = await apiGet(`/notifications?unread_only=${filter === 'unread'}`);
      setNotifications(response?.notifications || []);
      setUnreadCount(response?.unread_count || 0);
    } catch (error) {
      logError('NotificationsPanel.fetchNotifications', error);
      if (process.env.NODE_ENV === 'development') {
        console.warn(extractErrorMessage(error, 'Failed to load notifications'));
      }
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter, isOpen]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await apiPut(`/notifications/${notificationId}/read`, {});
      fetchNotifications();
    } catch (error) {
      logError('NotificationsPanel.markAsRead', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiPut('/notifications/mark-all-read', {});
      fetchNotifications();
    } catch (error) {
      logError('NotificationsPanel.markAllAsRead', error);
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return t('minutesAgo').replace('{n}', diffMins);
    if (diffHours < 24) return t('hoursAgo').replace('{n}', diffHours);
    return t('daysAgo').replace('{n}', diffDays);
  };

  const getTypeIcon = (type) => ({ info: '💡', warning: '⚠️', error: '❌', success: '✅', alert: '🔔' }[type] || '📌');
  const getCategoryColor = (category) => ({
    invoice: 'bg-blue-100 text-blue-800', payment: 'bg-green-100 text-green-800', inventory: 'bg-yellow-100 text-yellow-800',
    approval: 'bg-purple-100 text-purple-800', system: 'bg-gray-100 text-gray-800', financial: 'bg-indigo-100 text-indigo-800',
    hr: 'bg-pink-100 text-pink-800', manufacturing: 'bg-orange-100 text-orange-800'
  }[category] || 'bg-gray-100 text-gray-800');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-16" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-96 max-h-[80vh] overflow-hidden mx-4" onClick={(e) => e.stopPropagation()} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              {t('title')}
              {unreadCount > 0 && <span className="ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded-full">{unreadCount}</span>}
            </h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setFilter('all')} className={`px-3 py-1 text-sm rounded-full ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>{t('all')}</button>
            <button onClick={() => setFilter('unread')} className={`px-3 py-1 text-sm rounded-full ${filter === 'unread' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>{t('unread')}</button>
            {unreadCount > 0 && <button onClick={markAllAsRead} className="px-3 py-1 text-sm text-blue-600 hover:underline mr-auto">{t('markAllRead')}</button>}
          </div>
        </div>
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="p-8 text-center text-gray-500">{t('loading')}</div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500"><span className="text-4xl">🔔</span><p className="mt-2">{t('noNotifications')}</p></div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div key={notification.id} onClick={() => !notification.is_read && markAsRead(notification.id)} className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${!notification.is_read ? 'bg-blue-50' : ''}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{getTypeIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded ${getCategoryColor(notification.category)}`}>{t(notification.category) || notification.category}</span>
                        {!notification.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                      </div>
                      <h4 className="font-medium text-gray-800 text-sm">{language === 'ar' ? notification.title_ar : notification.title}</h4>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{language === 'ar' ? notification.message_ar : notification.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatTimeAgo(notification.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPanel;
